import { ImageUpload } from '#assets/svg';
import { Button, Flex, Input, Text, Textarea } from '#atoms';
import { usePriceInput } from '#hooks/usePriceInput';
import {
  FloatButtonContainer,
  LabeledInput,
  SessionAdderBox
} from '#molecules';
import { initialLecture } from '#providers/CoachLecture';
import { IPostCoachLecture, SessionType } from '#types/lectures';
import { formatNumberWithCommas } from '#utils/Numbers';
import { produce } from 'immer';
import { FormEvent, useEffect, useState } from 'react';
import CheckBoxAtom from 'src/components/atoms/Checkbox';
import { FileUpload } from 'src/shared/components/file-upload/FileUpload';
import styled from 'styled-components';
import { useSubmitCoachLecture } from './hooks/useSubmitCoachLecture';
import {
  Container,
  CurriculaArea,
  Label,
  MultiSesstionPriceContainer,
  RequiredMark
} from './styles';

export default function Normal() {
  const { price, handlePriceInput } = usePriceInput(undefined);
  const [form, setForm] = useState<IPostCoachLecture>(initialLecture);
  const [multiCountPrice, setMultiSessionDiscountPrice] = useState<
    SessionType[]
  >([{ session: 10, price: null }]);
  const [isMultiSessionDiscount, setIsMultiSesstionDiscount] =
    useState<boolean>(false);

  const revenue = price
    ? Math.floor((price - price * 0.033 * 1.1) / 10) * 10
    : 0;

  useEffect(() => {
    setForm(prev => ({ ...prev, price: price ?? 0, netRevenue: revenue }));
  }, [price]);

  const onFormChange = (value: Partial<IPostCoachLecture>) => {
    setForm(prev => ({
      ...prev,
      ...value
    }));
  };

  const addMultiSessionCount = () => {
    setMultiSessionDiscountPrice(prev => [
      ...prev,
      { session: (prev.length + 1) * 10, price: null }
    ]);
  };

  const handleMultiPriceInput = (e: string, index: number) => {
    const maxPrice = 1_000_000;
    if (e.charAt(0) === '0' && e.length > 1) return;
    if (e === '') {
      setMultiSessionDiscountPrice(prev =>
        prev.map((item, idx) =>
          idx === index ? { ...item, price: null } : item
        )
      );
      return;
    }
    const valueRaw = parseInt(e.replaceAll(',', ''), 10);
    if (!isNaN(valueRaw) && (maxPrice === undefined || valueRaw <= maxPrice)) {
      setMultiSessionDiscountPrice(prev =>
        prev.map((item, idx) =>
          idx === index ? { ...item, price: valueRaw } : item
        )
      );
    }
  };

  const { mutateAsync: submitLecture, isLoading } = useSubmitCoachLecture();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    handlePostButtonClick();
  };

  const handlePostButtonClick = async () => {
    await submitLecture({
      form,
      multiCountPrice
    });
  };

  // 파일 업로드 완료 시 호출되는 함수
  const handleUploadFileComplete = (id: string) => {
    setForm(
      produce(draft => {
        draft.images?.push(id);
      })
    );
  };

  // 파일 삭제 완료 시 호출되는 함수
  const handleDeleteFileComplete = (id: string) => {
    setForm(
      produce(draft => {
        draft.images = draft.images?.filter(v => v !== id);
      })
    );
  };

  return (
    <form onSubmit={handleSubmit}>
      <Container>
        <Flex direction="column" gap={8}>
          <Label>
            수업명<RequiredMark>*</RequiredMark>
          </Label>
          <Input
            type="text"
            placeholder="수업명을 입력해 주세요"
            value={form.title}
            maxLength={29}
            onChange={e => onFormChange({ title: String(e) })}
          />
          <Text
            textStyle="c1"
            textColor="gray2"
            style={{ textAlign: 'right' }}
          >{`${form.title.length} / 30`}</Text>
        </Flex>

        <Flex direction="column" gap={8}>
          <Label>
            수업 소개<RequiredMark>*</RequiredMark>
          </Label>
          <Textarea
            value={form.body}
            onChange={e => onFormChange({ body: e })}
            height={12.8}
            limit={400}
            placeholder="수업에 대해 소개해 주세요"
          />
        </Flex>

        <Flex direction="column" gap={16} justify="flex-start">
          <LabelInput
            label="회당 가격"
            labelStyle="t3"
            placeholder="가격을 입력해주세요"
            value={price !== undefined ? formatNumberWithCommas(price) : ''}
            suffix="원"
            onInput={handlePriceInput}
            required
          />

          <Flex direction="column" gap={20}>
            <Flex>
              <CheckBoxAtom
                id=""
                label="다회차 가격 할인"
                checked={isMultiSessionDiscount}
                onClick={() => setIsMultiSesstionDiscount(prev => !prev)}
                labelStyle={{ fontSize: '16px' }}
              />
            </Flex>
            {isMultiSessionDiscount && (
              <CurriculaArea>
                {multiCountPrice.map((item, index) => (
                  <MultiSesstionPriceContainer key={index}>
                    <Text children={`${item.session}회 이상`} />
                    <LabeledInput
                      labelStyle="t3"
                      placeholder="가격을 입력해주세요"
                      value={
                        typeof item.price === 'number'
                          ? formatNumberWithCommas(item.price)
                          : ''
                      }
                      suffix="원/회"
                      onInput={e => handleMultiPriceInput(e, index)}
                      label={''}
                    />
                  </MultiSesstionPriceContainer>
                ))}
                <SessionAdderBox onClickFuc={() => addMultiSessionCount()} />
              </CurriculaArea>
            )}
          </Flex>
        </Flex>

        <Flex direction="column" gap={8}>
          {/* <Label>수업 사진</Label> */}
          <div className="flex flex-col mt-20">
            <Label>수업 사진</Label>
            <FileUpload
              maxCountMsg={'사진은 최대 5개까지 첨부할 수 있어요!'}
              path={'image'}
              fileType="image/*"
              // onChange={onChange}
              maxCount={5}
              onUploadFileComplete={handleUploadFileComplete}
              onDeleteFileComplete={handleDeleteFileComplete}
            >
              <ImageUpload />
            </FileUpload>
          </div>
          {/* <ImageWithUpload2
            postedImages={postedImgs}
            localImages={localImgs}
            onUploadImage={handleUploadImages}
            onDeleteLocalImage={handleDeleteLocalImage}
            onDeletePostedImage={handleDeletePostedImage}
            maxCount={maxCount}
            fileType={videoFileType}
          /> */}
        </Flex>
      </Container>
      <FloatButtonContainer>
        <Button
          disabled={!(form.title && form.body && form.price) || isLoading}
        >
          등록
        </Button>
      </FloatButtonContainer>
    </form>
  );
}

const LabelInput = styled(LabeledInput).attrs(({ theme }) => ({
  textStyle: {
    color: theme.palette.gray25,
    fontWeight: 500
  }
}))``;
function setRecords(arg0: any) {
  throw new Error('Function not implemented.');
}
